import './table-hac-nominated.scss';
import React, {useEffect, useState} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import apiClient from '../../../../utils/apiClient';
import {useTranslation} from 'react-i18next';
import {Alert, Snackbar} from '@mui/material';
import Grid from "@mui/material/Grid2";
import decodeToken from "../../../../utils/decode-token";
import { useNavigate } from 'react-router-dom';
import {getEmailStatusLabel} from '../../../../utils/adminHacTableUtils';
import {compareDmyDates, sortRows} from '../../../../utils/tableUtils';
import useSummarizedFilteredRows from '../../common/table/hooks/use-summarized-filtered-rows';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';
import RowActionsMenu from '../../common/table/row-actions-menu';
import { deleteNominee } from '../../../../api/adminHacTableApi';
import ModalWindow from '../../common/modal/modal.js';

const SORT_COMPARATORS = {
  nomination_date: (a, b) => compareDmyDates(a.nomination_date, b.nomination_date),
};

function TableHacNominated({reload, onDataLoaded, onSummaryChange}) {
  const {t} = useTranslation();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRowsWithSummary] = useSummarizedFilteredRows(onSummaryChange);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [retailerOptions, setRetailerOptions] = useState([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState('');
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const userId = decodeToken(token).id;
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [nomineeToDelete, setNomineeToDelete] = useState(null);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // TODO: unused while the "delete nominee" menu option is hidden
  // eslint-disable-next-line no-unused-vars
  const handleOpenDeleteModal = (hacId) => {
    setNomineeToDelete(hacId);
    setDeleteModalOpen(true);
  };

  const handleDeleteNominee = async () => {
    try {
      await deleteNominee(token, nomineeToDelete);
      setSnackbarSeverity('success');
      setSnackbarMessage(t('admin-delete-nominee-success'));
      setSnackbarOpen(true);
      setDataLoaded(false);
    } catch (error) {
      setSnackbarSeverity('error');
      setSnackbarMessage(t('common-internal-server-error'));
      setSnackbarOpen(true);
    } finally {
      setDeleteModalOpen(false);
      setNomineeToDelete(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const authToken = token;
      const url = `${process.env.REACT_APP_API_URL}/hac/retailer-salesperson?status=nominated`;

      const config = {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Cache-Control': 'no-cache',
        },
      };

      try {
        const response = await apiClient.get(url, config);
        if (response.status === 200) {
          const sortedRows = sortRows(response.data, 'nomination_date', 'desc', SORT_COMPARATORS);

          const storedRetailerId = sessionStorage.getItem('filter-retailer');
          if (storedRetailerId) {
            const filtered = sortedRows.filter(row => row.retailer_info.id === Number(storedRetailerId));
            setFilteredRowsWithSummary(filtered);
            setSelectedRetailerId(storedRetailerId);
          } else {
            setFilteredRowsWithSummary(sortedRows);
          }
          setRows(sortedRows);
          onDataLoaded();
        } else if (response.status === 204) {
          handleEmptyTableResponse({
            clearStateSetters: [setRows, setFilteredRowsWithSummary],
            onDataLoaded,
          });
        }
      } catch (error) {
        if (error.response) {
          setSnackbarOpen(true);
          setSnackbarMessage(t('common-internal-server-error'), error);
        }
      } finally {
        setDataLoaded(true);
      }

      const userApiUrl = `${process.env.REACT_APP_API_URL}/retailer/staff-member?staffId=${userId}`;
      try {
        const userResponse = await apiClient.get(userApiUrl, config);
        setRetailerOptions(userResponse.data.retailerInfo || []);
      } catch (error) {
        console.error('Error al obtener datos del usuario:', error);
      }
    };

    if (reload || !dataLoaded) {
      fetchData();
    }
  }, [reload, dataLoaded, token, onDataLoaded, t, userId, setFilteredRowsWithSummary]);

  const handleApplyFilter = () => {
    sessionStorage.setItem('filter-retailer', selectedRetailerId);
    if (selectedRetailerId) {
      const filtered = rows.filter(row => row.retailer_info.id === Number(selectedRetailerId));
      setFilteredRowsWithSummary(filtered);
    } else {
      setFilteredRowsWithSummary(rows);
    }
  };

  return (
    <>
      {dataLoaded ? (
        rows && rows.length !== 0 ? (
          <div>
            <div className="intro">
              <div className="text">
                <h4>{t('retailer-main-nominated-title')}</h4>
                <p>{t('retailer-main-nominated-text')}</p>
              </div>
            </div>
            {retailerOptions.length > 1 && (
              <div className="filters">
                <p>{t('common-search')}</p>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <div className="form-control">
                      <label htmlFor="retailer">{t('common-retailer')}</label>
                      <select
                        name="retailer"
                        id="retailer"
                        className="normal"
                        value={selectedRetailerId}
                        onChange={(e) => setSelectedRetailerId(e.target.value)}
                      >
                        <option value="">{t('common-all-retailers')}</option>
                        {retailerOptions.map((retailer) => (
                          <option key={retailer.id} value={retailer.id}>
                            {retailer.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <div className="actions">
                      <button className="btn" onClick={handleApplyFilter}>
                        {t('common-apply')}
                      </button>
                    </div>
                  </Grid>
                </Grid>
              </div>
            )}
            {filteredRows.length !== 0 ? (
              <div className="table-wrapper">
                <Table
                  sx={{minWidth: role === 'retailerGM' ? 1060 : 900}}
                  aria-label="simple table"
                  className={`user-table retailer-nominated ${
                    role === 'retailerGM' ? 'retailerGM' : ''
                  }`}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">
                        <span className="text">{t('common-name')}</span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-last-name')}</span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-phone')}</span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-address')}</span>
                      </TableCell>
                      {role === 'retailerGM' && (
                        <TableCell align="left">
                          <span className="text">{t('common-retailer')}</span>
                        </TableCell>
                      )}
                      <TableCell align="left">
                        <span className="text">
                          {t('common-nomination-date')}
                        </span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-invitation')}</span>
                      </TableCell>
                      <TableCell align="center"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row) => {
                      const emailStatusLabel = getEmailStatusLabel(row);

                      return (
                      <TableRow key={row.id}>
                        <TableCell align="left">
                          <span className="text">{row.name}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">{row.last_name}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">
                            {row.prefix_phone} {row.phone}
                          </span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">
                            {row.has_address === 'true'
                              ? t('common-yes')
                              : t('common-no')}
                          </span>
                        </TableCell>
                        {role === 'retailerGM' && (
                          <TableCell align="left">
                            <span className="text">
                              {row.retailer_info.name} ({row.retailer_info.city}
                              )
                            </span>
                          </TableCell>
                        )}
                        <TableCell align="left">
                          <span className="text">{row.nomination_date}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">
                            {emailStatusLabel && <span>{t(emailStatusLabel)}</span>}
                            {/*
                            {row.email_status?.expired === 1  && (<strong className="alert"> - {t('common-expiring-soon')}</strong>)}
                            {row.email_status?.expired === 2  && (<strong className="expired"> - {t('common-expired')}</strong>)}
                            */}
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          <RowActionsMenu
                            actions={[
                              {
                                key: 'profile',
                                label: t('common-profile'),
                                onClick: () =>
                                  navigate(`/common/personal-details/${row.id}`, {
                                    state: {
                                      breadcrumbName: `${row.name} ${row.last_name}`,
                                    },
                                  }),
                              },
                              // TODO: re-enable "delete nominee" option
                              // {
                              //   key: 'delete',
                              //   label: t('common-remove'),
                              //   onClick: () => handleOpenDeleteModal(row.id),
                              // },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="no-result">
                <h2 className="txt-center">
                  <small>{t('admin-main-tab1-text-empty')}</small>
                </h2>
              </div>
            )}
          </div>
        ) : (
          <div className="no-result">
            <h2 className="txt-center">
              {t('retailer-main-nominated-empty-title')}
              <small>{t('retailer-main-nominated-empty-text')}</small>
            </h2>
          </div>
        )
      ) : (
        <div>{t('common-loading')}</div>
      )}
      <ModalWindow
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        executeFunction={handleDeleteNominee}
        postExecuteFunction={() => {}}
        title={t('admin-delete-nominee-title')}
        text={t('admin-delete-nominee-text')}
        textButtonCancel={t('common-cancel')}
        textoButtonConfirm={t('common-remove')}
        extraInfo={[]}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TableHacNominated;
